import * as Yup from "yup";
import { DOMINICAN_BOUNDS } from "@rf-l1/map-v2/helpers/bounds";

// Example: addMethod or any other custom stuff
Yup.addMethod<Yup.StringSchema>(Yup.string, "isNumeric", function () {
  return this.transform((value, originalValue) => {
    // Si el valor es un número, conviértelo a string
    if (typeof originalValue === "number") {
      return String(originalValue);
    }
    // Si es null o undefined, devuelve cadena vacía
    if (originalValue === null || originalValue === undefined) {
      return "";
    }
    // De lo contrario, devuelve el valor original
    return value;
  }).test(
    "isNumeric",
    "Value must be numeric",
    value => value === "" || !isNaN(Number(value))
  );
});

Yup.addMethod<Yup.StringSchema>(
  Yup.string,
  "minValue",
  function (val: number, error?: string) {
    return this.test(
      "minValue",
      error ?? `The field has to be greater or equal to $${val}`,
      original =>
        original === "undefined" ||
        original === null ||
        original === "" ||
        Number(original) >= val
    );
  }
);

Yup.addMethod<Yup.StringSchema>(
  Yup.string,
  "maxValue",
  function (val: number, error?: string) {
    return this.test(
      "maxValue",
      error ?? `The field has to be less or equal to $${val}`,
      original =>
        original === "undefined" ||
        original === null ||
        original === "" ||
        Number(original) <= val
    );
  }
);

Yup.addMethod<Yup.StringSchema>(
  Yup.string,
  "requiredWithIndex",
  function (fn: (index: number) => string) {
    return this.required(({ path }) => {
      const match = path.match(/\[(\d+)\]/);
      const index = match ? match[1] : "unknown";
      return fn(Number(index));
    });
  }
);

Yup.addMethod<Yup.StringSchema>(Yup.string, "transformNumeric", function () {
  return this.transform((value, originalValue) => {
    // Si el valor original es un número, conviértelo a string
    if (typeof originalValue === "number") {
      return String(originalValue);
    }

    // Si es null o undefined, devuelve cadena vacía
    if (originalValue === null || originalValue === undefined) {
      return "";
    }

    // En otros casos, mantén el valor original
    return value;
  });
});

Yup.addMethod<Yup.StringSchema>(Yup.string, "isInteger", function () {
  return this.transform((value, originalValue) => {
    // Si el valor es un número, conviértelo a string
    if (typeof originalValue === "number") {
      return String(originalValue);
    }
    // Si es null o undefined, devuelve cadena vacía
    if (originalValue === null || originalValue === undefined) {
      return "";
    }
    // En otros casos, mantén el valor original
    return value;
  }).test(
    "isInteger",
    "El campo debe ser un número entero",
    value => {
      if (value === "") return true;
      const v = String(value).trim();
      // Acepta sólo dígitos (con signo opcional). Rechaza notación decimal como "2.0" o con punto/coma.
      return /^-?\d+$/.test(v);
    }
  );
});

// Re-export everything so it behaves like the real Yup
export * from "yup";

type ExtendedYup = typeof Yup & {
  // Single-type builders: return StringSchema by default
  isLatDo: () => Yup.StringSchema;
  isLngDo: () => Yup.StringSchema;
};

// Helpers
function isLatWithinDO(val: unknown): boolean {
  const n = Number(val);
  if (!Number.isFinite(n)) return true; // let other validators handle required
  return n >= DOMINICAN_BOUNDS.south && n <= DOMINICAN_BOUNDS.north;
}
function isLngWithinDO(val: unknown): boolean {
  const n = Number(val);
  if (!Number.isFinite(n)) return true;
  return n >= DOMINICAN_BOUNDS.west && n <= DOMINICAN_BOUNDS.east;
}

// String methods
Yup.addMethod<Yup.StringSchema>(Yup.string, "isLatDo", function (message) {
  return this.test(
    "isLatDo",
    message || "La latitud debe estar dentro de la República Dominicana",
    (v: any) => isLatWithinDO(v)
  );
});

Yup.addMethod<Yup.StringSchema>(Yup.string, "isLngDo", function (message) {
  return this.test(
    "isLngDo",
    message || "La longitud debe estar dentro de la República Dominicana",
    (v: any) => isLngWithinDO(v)
  );
});

// Factory helpers attached to default export, returning StringSchema only
const extended = Yup as unknown as ExtendedYup & { __extended?: boolean };
if (!extended.__extended) {
  const lat = () => Yup.string().isNumeric().isLatDo();
  const lng = () => Yup.string().isNumeric().isLngDo();
  (extended as any).isLatDo = lat;
  (extended as any).isLngDo = lng;
  (extended as any).__extended = true;
}

export default extended as ExtendedYup;

declare module "yup" {
  interface StringSchema {
    isLatDo(message?: string): this;
    isLngDo(message?: string): this;
  }
}
